import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, Search, Star, Clock, Users, AlertCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { HelpSearch } from '@/components/HelpSearch';
import { HelpService } from '@/services/helpService';
import type { HelpArticle, HelpCategory } from '@/types/help';
import { logger } from '@/lib/logger';

const HelpPage: React.FC = () => {
  const navigate = useNavigate();
  const { articleSlug } = useParams<{ articleSlug?: string }>();
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState<HelpCategory[]>([]);
  const [currentArticle, setCurrentArticle] = useState<HelpArticle | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<HelpArticle[]>([]);
  const [featuredArticles, setFeaturedArticles] = useState<HelpArticle[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('browse');
  const [setupError, setSetupError] = useState<string | null>(null);

  // Initialize tab based on URL params and load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      setSetupError(null);
      
      // Set active tab based on URL
      const tab = searchParams.get('tab');
      if (tab && ['browse', 'search'].includes(tab)) {
        setActiveTab(tab);
      } else if (articleSlug) {
        setActiveTab('article');
      } else {
        setActiveTab('browse'); // Default to browse
      }
      
      try {
        const [categoriesData, featuredData] = await Promise.all([
          HelpService.getCategories(),
          HelpService.getFeaturedArticles(6),
        ]);
        
        setCategories(categoriesData);
        setFeaturedArticles(featuredData);
        
        // Check if help system is set up
        if (categoriesData.length === 0 && featuredData.length === 0) {
          setSetupError("Help system is not yet configured. Please run the database migration and populate help articles.");
        }
      } catch (error) {
        logger.error('Error loading help data:', error);
        setSetupError("Unable to load help content. The help system may not be set up yet.");
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [searchParams, articleSlug]); // Reload when URL changes

  // Load specific article if articleSlug is provided
  useEffect(() => {
    const loadArticle = async () => {
      if (!articleSlug) {
        setCurrentArticle(null);
        setRelatedArticles([]);
        return;
      }

      setIsLoading(true);
      try {
        const article = await HelpService.getArticleBySlug(articleSlug);
        if (article) {
          setCurrentArticle(article);
          setActiveTab('article');
          
          // Record article view
          HelpService.recordArticleView(article.id);
          
          // Load related articles
          const related = await HelpService.getRelatedArticles(article, 3);
          setRelatedArticles(related);
        } else {
          // Article not found, redirect to help home
          navigate('/help', { replace: true });
        }
      } catch (error) {
        logger.error('Error loading article:', error);
        navigate('/help', { replace: true });
      } finally {
        setIsLoading(false);
      }
    };

    loadArticle();
  }, [articleSlug, navigate]);

  const handleArticleSelect = (slug: string) => {
    navigate(`/help/${slug}`);
  };

  const handleCategorySelect = async (categorySlug: string) => {
    try {
      const articles = await HelpService.getArticlesByCategory(categorySlug);
      if (articles.length > 0) {
        handleArticleSelect(articles[0].slug);
      } else {
        // No articles found, switch to search tab with category filter
        setActiveTab('search');
        navigate(`/help?tab=search&category=${categorySlug}`);
      }
    } catch (error) {
      logger.error('Error loading category articles:', error);
      // On error, switch to search tab with category filter
      setActiveTab('search');
      navigate(`/help?tab=search&category=${categorySlug}`);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-grow container mx-auto py-8 px-4">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-grow container mx-auto py-8 px-4 max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Button 
                variant="outline" 
                onClick={() => navigate(-1)}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <div>
                <h1 className="text-3xl font-bold">Help Center</h1>
                <p className="text-muted-foreground">
                  Find answers to your questions and learn how to use FAB
                </p>
              </div>
            </div>
            <BookOpen className="h-12 w-12 text-primary opacity-50" />
          </div>
        </div>

        {/* Setup Error Alert */}
        {setupError && (
          <Alert className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Help System Setup Required</AlertTitle>
            <AlertDescription>
              {setupError}
              <br />
              <span className="text-sm text-muted-foreground mt-2 block">
                See the setup guide in <code>scripts/setup-help-system.md</code> for instructions.
              </span>
            </AlertDescription>
          </Alert>
        )}

        {/* Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 lg:w-[400px]">
            <TabsTrigger value="browse" className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Browse
            </TabsTrigger>
            <TabsTrigger value="search" className="flex items-center gap-2">
              <Search className="h-4 w-4" />
              Search
            </TabsTrigger>
          </TabsList>

          {/* Browse Tab */}
          <TabsContent value="browse" className="space-y-6 mt-6">
            {/* Fallback content when no data is available */}
            {!isLoading && categories.length === 0 && featuredArticles.length === 0 && (
              <div className="text-center py-12">
                <BookOpen className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-xl font-semibold mb-2">Help Content - Future Enhancement</h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  We're setting up our comprehensive help documentation. In the meantime, feel free to contact support for any questions.
                </p>
                <div className="mt-6 space-x-4">
                  <Button onClick={() => navigate('/about')}>About FAB</Button>
                  <Button variant="outline" onClick={() => setActiveTab('search')}>
                    Try Search
                  </Button>
                </div>
              </div>
            )}
            
            {/* Featured Articles */}
            {featuredArticles.length > 0 && (
              <section>
                <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                  <Star className="h-5 w-5" />
                  Featured Articles
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {featuredArticles.map(article => (
                    <Card 
                      key={article.id}
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => handleArticleSelect(article.slug)}
                    >
                      <CardHeader>
                        <CardTitle className="text-lg">{article.title}</CardTitle>
                        {article.excerpt && (
                          <CardDescription className="line-clamp-2">
                            {article.excerpt}
                          </CardDescription>
                        )}
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">
                            {categories.find(c => c.slug === article.category)?.name || article.category}
                          </Badge>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {new Date(article.updated_at).toLocaleDateString()}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>
            )}

            {/* Categories */}
            <section>
              <h2 className="text-2xl font-semibold mb-4">Browse by Category</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {categories.map(category => (
                  <Card 
                    key={category.id}
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => handleCategorySelect(category.slug)}
                  >
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        {category.icon_name && (
                          <div className="h-5 w-5 text-primary">
                            {/* You can add icon mapping here based on icon_name */}
                            <BookOpen className="h-5 w-5" />
                          </div>
                        )}
                        {category.name}
                      </CardTitle>
                      {category.description && (
                        <CardDescription>{category.description}</CardDescription>
                      )}
                    </CardHeader>
                  </Card>
                ))}
              </div>
            </section>
          </TabsContent>

          {/* Search Tab */}
          <TabsContent value="search" className="mt-6">
            <div className="max-w-4xl">
              <HelpSearch 
                onArticleSelect={handleArticleSelect}
                placeholder="Search our help documentation..."
                className="w-full"
              />
            </div>
          </TabsContent>

          {/* Article Tab */}
          <TabsContent value="article" className="mt-6">
            {currentArticle && (
              <div className="max-w-4xl">
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge variant="secondary">
                      {categories.find(c => c.slug === currentArticle.category)?.name || currentArticle.category}
                    </Badge>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      Last updated {new Date(currentArticle.updated_at).toLocaleDateString()}
                    </div>
                  </div>
                  <h1 className="text-3xl font-bold mb-4">{currentArticle.title}</h1>
                  {currentArticle.tags && currentArticle.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-6">
                      {currentArticle.tags.map(tag => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <Card>
                  <CardContent className="p-6">
                    <div className="prose prose-sm max-w-none dark:prose-invert">
                      <ReactMarkdown
                        components={{
                          a: ({ href, ...props }) => {
                            const isExternal = Boolean(href && /^https?:\/\//.test(href));

                            return (
                              <a
                                {...props}
                                href={href}
                                target={isExternal ? "_blank" : undefined}
                                rel={isExternal ? "noreferrer noopener" : undefined}
                                className="font-medium text-primary underline underline-offset-2"
                              />
                            );
                          },
                          img: ({ src, alt, ...props }) => (
                            <img
                              {...props}
                              src={src}
                              alt={alt || ""}
                              loading="lazy"
                              className="my-6 w-full rounded-xl border border-border shadow-sm"
                            />
                          ),
                        }}
                      >
                        {currentArticle.content}
                      </ReactMarkdown>
                    </div>
                  </CardContent>
                </Card>

                {/* Related Articles */}
                {relatedArticles.length > 0 && (
                  <section className="mt-8">
                    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      Related Articles
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {relatedArticles.map(article => (
                        <Card 
                          key={article.id}
                          className="cursor-pointer hover:shadow-md transition-shadow"
                          onClick={() => handleArticleSelect(article.slug)}
                        >
                          <CardHeader>
                            <CardTitle className="text-base">{article.title}</CardTitle>
                            {article.excerpt && (
                              <CardDescription className="line-clamp-2 text-sm">
                                {article.excerpt}
                              </CardDescription>
                            )}
                          </CardHeader>
                        </Card>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
      <Footer />
    </div>
  );
};

export default HelpPage;
